import type {
  CalendarMeeting,
  CalendarProvider,
  MeetingBriefAgenda,
  MeetingBriefGetResponse,
  MeetingBriefPayload,
  MeetingBriefPreviousMinutes,
  MeetingBriefStatus,
  MeetingBriefSummary,
  MeetingBriefSummaryResponse,
  MeetingBriefTicketsUnavailableReason,
  SetMeetingBriefProjectResponse,
} from '@ai-meeting/shared'
import { apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'

/** A calendar meeting, tagged with the calendar it came from. */
export interface Meeting extends CalendarMeeting {
  provider: CalendarProvider
}

/** A Backlog project the reader can brief a meeting against. */
export interface Project {
  projectKey: string
  name: string
  client?: string
}

export type BriefTicketStatus = 'Open' | 'In Progress' | 'Resolved' | 'Closed'

export interface BriefAssignee {
  name: string
  initials: string
}

export interface BriefBacklogTicket {
  key: string
  title: string
  status: string
  group?: 'done' | 'overdue' | 'upcoming'
  assignee: BriefAssignee
  dueDate: string | null
  resolvedAt: string | null
  url: string
}

export interface BriefAssignedItem {
  content: string
  assignee: string
  deadline: string
  issueKey: string | null
}

export interface BriefAssignedTasks {
  count: number
  ticketsCreated: boolean
  ticketKeys: string[]
  items: BriefAssignedItem[]
}

export interface BriefPreviousMeeting {
  id: string
  number: number
  title: string
  heldAt: string
  durationMinutes: number
  minutesUrl: string
  /** Local `Meeting.id` when the brief maps to a row, even if the viewer cannot open it yet. */
  openMeetingId: string | null
  /** AI payload `meetings[0].meeting_id` for click-time access checks when resolve missed a row. */
  referencedMeetingId: string | null
  accessible: boolean
  owner: { name: string; email: string } | null
}

export interface BriefCommentSummary {
  ticketKey: string
  summary: string
  commentCount: number
  commenters: string[]
  latestAt: string
}

export interface BriefData {
  meetingId: string
  project: Project
  generatedAt: string
  language: string
  /** Resolved local `Meeting.id` for the brief's previous meeting, if any. */
  previousMeetingLocalId: string | null
  previousMeeting: BriefPreviousMeeting
  summary: string
  agreedPoints: string[]
  assignedTasks: BriefAssignedTasks
  agenda: MeetingBriefAgenda
  backlogTickets: BriefBacklogTicket[]
  commentSummaries: BriefCommentSummary[]
  ticketsUnavailableReason: MeetingBriefTicketsUnavailableReason | null
}

export type { MeetingBriefStatus, MeetingBriefTicketsUnavailableReason }

/** Fixture list for tests and for suggesting a key when no API list is loaded. */
export const BRIEF_PROJECTS: Project[] = [
  { projectKey: 'VAOS', name: 'VAOS', client: 'Internal platform' },
  { projectKey: 'B2B', name: 'B2B payment gateway', client: 'NEC Solutions' },
  { projectKey: 'LTP', name: 'Logistics tracking portal', client: 'Yamato Systems' },
  { projectKey: 'HRB', name: 'HR chatbot', client: 'SunCo' },
  { projectKey: 'BRSE', name: 'BrSE workspace', client: 'S1.AI' },
]

function briefPath(provider: CalendarProvider, eventId: string): string {
  return `/api/calendar/${provider}/events/${encodeURIComponent(eventId)}/brief`
}

/** The project sub-route, kept next to {@link briefPath} so both stay prefixed. */
function briefProjectPath(provider: CalendarProvider, eventId: string): string {
  return `${briefPath(provider, eventId)}/project`
}

export async function fetchMeetingBrief(
  provider: CalendarProvider,
  eventId: string,
  options: { signal?: AbortSignal } = {}
): Promise<MeetingBriefGetResponse> {
  try {
    const { data } = await apiClient.get<MeetingBriefGetResponse>(briefPath(provider, eventId), {
      signal: options.signal,
    })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetings)
  }
}

function briefStatusPath(provider: CalendarProvider): string {
  return `/api/calendar/${provider}/briefs/status`
}

export async function fetchBriefStatuses(
  provider: CalendarProvider,
  eventIds: string[],
  options: { signal?: AbortSignal } = {}
): Promise<MeetingBriefSummary[]> {
  if (eventIds.length === 0) return []
  try {
    const { data } = await apiClient.post<MeetingBriefSummaryResponse>(
      briefStatusPath(provider),
      { eventIds: eventIds.slice(0, 200) },
      { signal: options.signal }
    )
    return data.items
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetings)
  }
}

export async function setBriefProject(
  provider: CalendarProvider,
  eventId: string,
  projectKey: string,
  options: { refresh?: boolean; signal?: AbortSignal } = {}
): Promise<SetMeetingBriefProjectResponse> {
  try {
    const { data } = await apiClient.put<SetMeetingBriefProjectResponse>(
      briefProjectPath(provider, eventId),
      { projectKey, refresh: options.refresh === true },
      { signal: options.signal }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetings)
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/)
  return (parts[parts.length - 1]?.[0] ?? '?').toUpperCase()
}

/**
 * Map a stored AI payload into the shape the popup already renders.
 *
 * `previousMeetingId` is the backend's resolved `Meeting.id` for this brief's
 * previous meeting, not the AI's `meeting_id`: the latter comes from another
 * service and need not name a row of ours, so linking to it would 404 while
 * the brief itself looked fine. No resolved id means no minutes page to open.
 */
export function toBriefData(
  meeting: Meeting,
  project: Project,
  payload: MeetingBriefPayload,
  previousMeetingId: string | null = null,
  previousMinutes: MeetingBriefPreviousMinutes | null = null
): BriefData {
  const previous = payload.meetings[0]
  const heldAt = previous?.held_at || meeting.startsAt
  const durationMinutes = Math.max(1, Math.round((previous?.duration_seconds ?? 0) / 60)) || 0
  const openMeetingId = previousMinutes?.meetingId ?? null
  const referencedMeetingId = previous?.meeting_id?.trim() || null
  const previousTitle = previousMinutes?.title ?? previous?.title ?? meeting.title
  const accessible = previousMinutes?.accessible ?? Boolean(previousMeetingId)
  const owner = previousMinutes?.owner ?? null
  const items: BriefAssignedItem[] = payload.assigned.items.map((item) => ({
    content: item.content,
    assignee: item.assignee,
    deadline: item.deadline,
    issueKey: item.issue_key,
  }))
  const ticketKeys = items.flatMap((item) => (item.issueKey ? [item.issueKey] : []))
  const emptyAgenda: MeetingBriefAgenda = {
    progress: [],
    decisions: [],
    questions: [],
    risks: [],
  }
  const agenda = payload.agenda ?? emptyAgenda

  return {
    meetingId: meeting.id,
    project,
    generatedAt: payload.generated_at,
    language: payload.language,
    previousMeetingLocalId: previousMeetingId,
    previousMeeting: {
      id: previousMeetingId ?? previous?.meeting_id ?? meeting.id,
      number: 0,
      title: previousTitle,
      heldAt,
      durationMinutes,
      minutesUrl: previousMeetingId ? `/meetings/${previousMeetingId}` : '/history',
      openMeetingId,
      referencedMeetingId,
      accessible,
      owner,
    },
    summary: payload.summary,
    agreedPoints: payload.agreed,
    assignedTasks: {
      count: payload.assigned.total || items.length,
      ticketsCreated: payload.assigned.with_ticket > 0,
      ticketKeys,
      items,
    },
    agenda,
    backlogTickets: payload.tickets.map((ticket) => ({
      key: ticket.issue_key,
      title: ticket.title,
      status: ticket.status,
      group: ticket.group,
      assignee: { name: ticket.assignee, initials: initialsOf(ticket.assignee) },
      dueDate: ticket.due_date,
      resolvedAt: null,
      url: ticket.url,
    })),
    commentSummaries: payload.comments.map((comment) => ({
      ticketKey: comment.issue_key,
      summary: comment.summary,
      commentCount: comment.count,
      commenters: comment.authors,
      latestAt: comment.last_at,
    })),
    ticketsUnavailableReason: payload.tickets_unavailable_reason,
  }
}

function normalizeTitle(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function scoreProjectMatch(meetingTitle: string, project: Project): number {
  const title = ` ${normalizeTitle(meetingTitle)} `
  let score = 0
  const key = normalizeTitle(project.projectKey)
  if (key.length >= 2 && title.includes(` ${key} `)) score += 4

  for (const token of normalizeTitle(project.name).split(' ')) {
    if (token.length >= 3 && title.includes(token)) score += 1
  }

  return score
}

/**
 * The project the meeting's own name points at.
 *
 * A project key inside the title outranks a word from the project name.
 * Falls back to the first project so the picker still highlights a choice.
 */
export function suggestProject(
  meetingTitle: string,
  projects: Project[] = BRIEF_PROJECTS
): Project | null {
  if (projects.length === 0) return null
  let best = projects[0]
  let bestScore = 0

  for (const project of projects) {
    const score = scoreProjectMatch(meetingTitle, project)
    if (score > bestScore) {
      best = project
      bestScore = score
    }
  }

  return best
}

/**
 * Same scoring as {@link suggestProject}, but returns null when the title
 * names no project clearly — used for calendar Brief eligibility, not the picker.
 */
export function suggestProjectStrict(
  meetingTitle: string,
  projects: Project[] = BRIEF_PROJECTS
): Project | null {
  if (projects.length === 0) return null
  let best: Project | null = null
  let bestScore = 0

  for (const project of projects) {
    const score = scoreProjectMatch(meetingTitle, project)
    if (score > bestScore) {
      best = project
      bestScore = score
    }
  }

  return bestScore > 0 ? best : null
}

/** The meeting the brief is written from. Daily series look one day back. */
export function getPreviousMeetingDate(meeting: Meeting): Date {
  const starts = new Date(meeting.startsAt)
  if (Number.isNaN(starts.getTime())) return new Date()
  const next = new Date(starts)
  next.setDate(next.getDate() + (/daily/i.test(meeting.title) ? -1 : -7))
  return next
}

export function toBriefMeeting(meeting: CalendarMeeting, provider: CalendarProvider): Meeting {
  return { ...meeting, provider }
}

/** A ticket is overdue only while it still has work left and its due day has passed. */
export function isTicketOverdue(ticket: BriefBacklogTicket, now: Date): boolean {
  if (ticket.group === 'overdue') return true
  if (ticket.group === 'done' || ticket.status === 'Resolved' || ticket.status === 'Closed') {
    return false
  }
  if (!ticket.dueDate) return false
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return ticket.dueDate < `${year}-${month}-${day}`
}
